import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { LocalDate } from "@js-joda/core";
import type { StaffProfileData } from "services/api/company/staff.service";
import type { EmploymentDetailsForm } from "../../profilePanel.contract";
import type { ModalState, TranslateFn } from "../profilePanel.state.contract";
import {
  toEmployeeNumberForNewEmployment,
  toEmploymentDetailsForm,
} from "../profilePanel.state.formMappers.logic";
import {
  confirmEndEmploymentUpdate as runConfirmEndEmploymentUpdate,
  toSuggestedEndEmploymentDate,
} from "../profilePanel.state.endEmployment.logic";
import { EmploymentUiRules } from "../../../shared/staffEmploymentRules.logic";

interface UseProfilePanelEndEmploymentModalStateParams {
  companyId: number | null;
  currentEmploymentStartDate: LocalDate | null;
  endEmploymentDate: LocalDate | null;
  employmentRules: EmploymentUiRules;
  isOpen: boolean;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  reloadProfile: () => void;
  setEndEmploymentDate: Dispatch<SetStateAction<LocalDate | null>>;
  setIsSaving: (value: boolean) => void;
  setModalState: Dispatch<SetStateAction<ModalState>>;
  setSaveError: (value: string) => void;
  translate: TranslateFn;
  workerId: number | null;
  onStartCreatingEmployment: (details: EmploymentDetailsForm) => void;
}

interface UseProfilePanelEndEmploymentModalStateResult {
  hasEndedEmploymentForCreateFlow: boolean;
  handleOpenEndEmploymentModal: () => void;
  openEndEmploymentForCreateModal: () => void;
  handleCloseEndEmploymentModal: () => void;
  handleConfirmEndEmploymentModal: () => void;
  handleCloseEndEmploymentForCreateModal: () => void;
  handleConfirmEndEmploymentForCreateModal: () => void;
}

export const useProfilePanelEndEmploymentModalState = (
  params: UseProfilePanelEndEmploymentModalStateParams,
): UseProfilePanelEndEmploymentModalStateResult => {
  const [hasEndedEmploymentForCreateFlow, setHasEndedEmploymentForCreateFlow] =
    useState(false);

  useEffect(() => {
    if (!params.isOpen) {
      setHasEndedEmploymentForCreateFlow(false);
      return;
    }

    setHasEndedEmploymentForCreateFlow(false);
  }, [params.isOpen, params.workerId]);

  const confirmEndEmploymentUpdate = useCallback(
    (onSuccess: () => void): void => {
      runConfirmEndEmploymentUpdate({
        companyId: params.companyId,
        employmentRules: params.employmentRules,
        endEmploymentDate: params.endEmploymentDate,
        isSaving: params.isSaving,
        onSuccess,
        onWorkerUpdated: params.onWorkerUpdated,
        profile: params.profile,
        reloadProfile: params.reloadProfile,
        setIsSaving: params.setIsSaving,
        setSaveError: params.setSaveError,
        translate: params.translate,
        workerId: params.workerId,
      });
    },
    [params],
  );

  const handleOpenEndEmploymentModal = useCallback((): void => {
    if (params.isSaving) return;

    params.setEndEmploymentDate(
      toSuggestedEndEmploymentDate(params.currentEmploymentStartDate),
    );
    params.setSaveError("");
    params.setModalState({ type: "endEmployment" });
  }, [params]);

  const openEndEmploymentForCreateModal = useCallback((): void => {
    params.setEndEmploymentDate(
      toSuggestedEndEmploymentDate(params.currentEmploymentStartDate),
    );
    params.setSaveError("");
    params.setModalState({ type: "endEmploymentForCreate" });
  }, [params]);

  const handleCloseEndEmploymentModal = useCallback((): void => {
    if (params.isSaving) return;
    params.setSaveError("");
    params.setModalState({ type: "none" });
  }, [params]);

  const handleConfirmEndEmploymentModal = useCallback((): void => {
    confirmEndEmploymentUpdate((): void => {
      params.setModalState({ type: "none" });
    });
  }, [confirmEndEmploymentUpdate, params]);

  const handleCloseEndEmploymentForCreateModal = useCallback((): void => {
    if (params.isSaving) return;
    params.setSaveError("");
    params.setModalState({ type: "none" });
  }, [params]);

  const handleConfirmEndEmploymentForCreateModal = useCallback((): void => {
    confirmEndEmploymentUpdate((): void => {
      params.setModalState({ type: "none" });
      const employmentDetails = toEmploymentDetailsForm(params.profile);
      params.onStartCreatingEmployment({
        ...employmentDetails,
        employeeNumber: toEmployeeNumberForNewEmployment(params.profile),
      });
      setHasEndedEmploymentForCreateFlow(true);
    });
  }, [confirmEndEmploymentUpdate, params]);

  return {
    hasEndedEmploymentForCreateFlow,
    handleOpenEndEmploymentModal,
    openEndEmploymentForCreateModal,
    handleCloseEndEmploymentModal,
    handleConfirmEndEmploymentModal,
    handleCloseEndEmploymentForCreateModal,
    handleConfirmEndEmploymentForCreateModal,
  };
};
